import { useEffect } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import { StandardPerk, TradeoffPerk, UltimatePerk } from 'types/perks';
import { Setters, States } from 'types/states';
import {
  useIntegerState,
  useCheckboxState,
  useFloatState,
  useUpdatedState,
} from 'utils/hooks';
import { getInitialResults, useSetLog, useSetResults } from './results';

export type GameStateContextType = States & Setters;

const noop = () => {};

const defaultBannedPerks = [];
const defaultPriorityList = [
  TradeoffPerk.COINS_HEALTH,
  StandardPerk.PERK_WAVE_REQUIREMENT,
  UltimatePerk.GOLDEN_TOWER,
  StandardPerk.INCREASE_GAME_SPEED,
  StandardPerk.COIN_BONUSES,
  TradeoffPerk.DAMAGE_DAMAGE,
  TradeoffPerk.DAMAGE_BOSS_HEALTH,
  StandardPerk.ORBS,
  StandardPerk.DEFENSE_PERCENT,
  StandardPerk.MAX_HEALTH,
  StandardPerk.UNLOCK_ULTIMATE_WEAPON,
  UltimatePerk.CHAIN_LIGHTNING,
  UltimatePerk.CHRONO_FIELD,
  UltimatePerk.DEATH_WAVE,
  UltimatePerk.INNER_LAND_MINES,
  UltimatePerk.POISON_SWAMP,
  UltimatePerk.SMART_MISSILES,
  StandardPerk.DAMAGE,
  StandardPerk.BOUNCE_SHOT,
  StandardPerk.HEALTH_REGEN,
  StandardPerk.INTEREST,
  StandardPerk.LAND_MINE_DAMAGE,
  TradeoffPerk.LIFESTEAL_KNOCKBACK,
  TradeoffPerk.BOSS_HEALTH_SPEED,
  TradeoffPerk.CASH_WAVE_KILL,
  TradeoffPerk.HEALTH_REGEN_LIFESTEAL,
  TradeoffPerk.RANGE_RANGE,
  TradeoffPerk.SPEED_DAMAGE,
  TradeoffPerk.REGEN_HEALTH,
  StandardPerk.FREE_UPGRADE_CHANCE,
];
const defaultNeverPick = [
  TradeoffPerk.SPEED_DAMAGE,
  TradeoffPerk.REGEN_HEALTH,
  TradeoffPerk.LIFESTEAL_KNOCKBACK,
  StandardPerk.FREE_UPGRADE_CHANCE,
  TradeoffPerk.BOSS_HEALTH_SPEED,
  TradeoffPerk.CASH_WAVE_KILL,
  TradeoffPerk.RANGE_RANGE,
  TradeoffPerk.HEALTH_REGEN_LIFESTEAL,
];

export const GameState = createContext<GameStateContextType>({
  simulationCount: 0,
  maxWave: 4000,

  smartMissilesUnlocked: false,
  deathWaveUnlocked: false,
  chainLightningUnlocked: false,
  chronoFieldUnlocked: false,
  goldenTowerUnlocked: false,
  innerLandMinesUnlocked: false,
  poisonSwampUnlocked: false,
  blackHoleUnlocked: false,
  spotlightUnlocked: false,

  standardResearch: 0,
  tradeoffResearch: 0,
  waveReductionResearch: 0,
  perkOptionResearch: 0,

  coinBonuses: 12.6,
  coinsPerKill: 1,
  coinsPerWave: 0,
  goldenTowerBonus: 5,
  goldenTowerDuration: 15,
  goldenTowerCooldown: 300,
  spotlightQuantity: 1,
  spotlightAngle: 30,
  spotlightCoins: 3,
  blackHoleCoverage: 12.5,
  blackHoleCoins: 1,
  blackHoleDuration: 15,
  blackHoleCooldown: 200,
  criticalCoin: 30,

  firstPerkChoice: undefined,
  bannedPerks: [],
  perkPriorityList: [],
  neverPickPerks: [],

  gameSpeed: 5,
  waveSkip: 17,
  introSprint: 0,
  autopick: false,
  waveAccelerator: 0,

  setAutopick: noop,
  setWaveAccelerator: noop,

  setSimulationCount: noop,
  setMaxWave: noop,

  setSmartMissilesUnlocked: noop,
  setDeathWaveUnlocked: noop,
  setChainLightningUnlocked: noop,
  setChronoFieldUnlocked: noop,
  setGoldenTowerUnlocked: noop,
  setInnerLandMinesUnlocked: noop,
  setPoisonSwampUnlocked: noop,
  setBlackHoleUnlocked: noop,
  setSpotlightUnlocked: noop,

  setStandardResearch: noop,
  setTradeoffResearch: noop,
  setWaveReductionResearch: noop,
  setPerkOptionResearch: noop,

  setCoinBonuses: noop,
  setCoinsPerKill: noop,
  setCoinsPerWave: noop,
  setGoldenTowerBonus: noop,
  setGoldenTowerDuration: noop,
  setGoldenTowerCooldown: noop,
  setSpotlightQuantity: noop,
  setSpotlightAngle: noop,
  setSpotlightCoins: noop,
  setBlackHoleCoverage: noop,
  setBlackHoleCoins: noop,
  setBlackHoleDuration: noop,
  setBlackHoleCooldown: noop,
  setCriticalCoin: noop,

  setFirstPerkChoice: noop,
  setBannedPerks: noop,
  setPerkPriorityList: noop,
  setNeverPickPerks: noop,
  setGameSpeed: noop,
  setWaveSkip: noop,
  setIntroSprint: noop,
});

const { Provider } = GameState;

export const GameStateProvider = ({ children }) => {
  const setResults = useSetResults();
  const setLog = useSetLog();
  const [simulationCount, setSimulationCount] = useIntegerState(
    10_000,
    'simulationCount',
  );
  const [maxWave, setMaxWave] = useIntegerState(4000, 'maxWave');

  const [smartMissilesUnlocked, setSmartMissilesUnlocked] = useCheckboxState(
    false,
    'smartMissilesUnlocked',
  );
  const [deathWaveUnlocked, setDeathWaveUnlocked] = useCheckboxState(
    false,
    'deathWaveUnlocked',
  );
  const [chainLightningUnlocked, setChainLightningUnlocked] = useCheckboxState(
    false,
    'chainLightningUnlocked',
  );
  const [chronoFieldUnlocked, setChronoFieldUnlocked] = useCheckboxState(
    false,
    'chronoFieldUnlocked',
  );
  const [goldenTowerUnlocked, setGoldenTowerUnlocked] = useCheckboxState(
    false,
    'goldenTowerUnlocked',
  );
  const [innerLandMinesUnlocked, setInnerLandMinesUnlocked] = useCheckboxState(
    false,
    'innerLandMinesUnlocked',
  );
  const [poisonSwampUnlocked, setPoisonSwampUnlocked] = useCheckboxState(
    false,
    'poisonSwampUnlocked',
  );
  const [blackHoleUnlocked, setBlackHoleUnlocked] = useCheckboxState(
    false,
    'blackHoleUnlocked',
  );
  const [spotlightUnlocked, setSpotlightUnlocked] = useCheckboxState(
    false,
    'spotlightUnlocked',
  );

  const [standardResearch, setStandardResearch] = useIntegerState(
    0,
    'standardResearch',
  );
  const [tradeoffResearch, setTradeoffResearch] = useIntegerState(
    0,
    'tradeoffResearch',
  );
  const [waveReductionResearch, setWaveReductionResearch] = useIntegerState(
    0,
    'waveReductionResearch',
  );
  const [perkOptionResearch, setPerkOptionResearch] = useIntegerState(
    1,
    'perkOptionResearch',
  );

  const [coinBonuses, setCoinBonuses] = useFloatState(12.6, 'coinBonuses');
  const [coinsPerKill, setCoinsPerKill] = useFloatState(1, 'coinsPerKill');
  const [coinsPerWave, setCoinsPerWave] = useIntegerState(0, 'coinsPerWave');
  const [goldenTowerBonus, setGoldenTowerBonus] = useFloatState(
    5,
    'goldenTowerBonus',
  );
  const [goldenTowerDuration, setGoldenTowerDuration] = useIntegerState(
    15,
    'goldenTowerDuration',
  );
  const [goldenTowerCooldown, setGoldenTowerCooldown] = useIntegerState(
    300,
    'goldenTowerCooldown',
  );
  const [blackHoleCoverage, setBlackHoleCoverage] = useFloatState(
    12.5,
    'blackHoleCoverage',
  );
  const [spotlightCoins, setSpotlightCoins] = useFloatState(
    3,
    'spotlightCoins',
  );
  const [blackHoleCoins, setBlackHoleCoins] = useFloatState(
    1,
    'blackHoleCoins',
  );
  const [blackHoleDuration, setBlackHoleDuration] = useIntegerState(
    15,
    'blackHoleDuration',
  );
  const [spotlightQuantity, setSpotlightQuantity] = useIntegerState(
    1,
    'spotlightQuantity',
  );
  const [spotlightAngle, setSpotlightAngle] = useIntegerState(
    30,
    'spotlightAngle',
  );
  const [blackHoleCooldown, setBlackHoleCooldown] = useIntegerState(
    200,
    'blackHoleCooldown',
  );
  const [criticalCoin, setCriticalCoin] = useIntegerState(30, 'criticalCoin');

  const [firstPerkChoice, setFirstPerkChoice] = useUpdatedState(
    null,
    'firstPerkChoice',
  );
  const [bannedPerks, setBannedPerks] = useUpdatedState(
    defaultBannedPerks,
    'bannedPerks',
  );
  const [perkPriorityList, setPerkPriorityList] = useUpdatedState(
    defaultPriorityList,
    'priorityList',
  );
  const [neverPickPerks, setNeverPickPerks] = useUpdatedState(
    defaultNeverPick,
    'neverPickPerks',
  );
  const [gameSpeed, setGameSpeed] = useFloatState(5, 'gameSpeed');
  const [waveSkip, setWaveSkip] = useIntegerState(17, 'waveSkip');
  const [waveAccelerator, setWaveAccelerator] = useIntegerState(
    54,
    'waveAccelerator',
  );
  const [autopick, setAutopick] = useCheckboxState(false, 'autopick');
  const [introSprint, setIntroSprint] = useIntegerState(0, 'introSprint');

  useEffect(() => {
    setLog([]);
    setResults(getInitialResults());
  }, [
    setResults,
    setLog,
    maxWave,
    smartMissilesUnlocked,
    deathWaveUnlocked,
    chainLightningUnlocked,
    autopick,
    waveAccelerator,
    chronoFieldUnlocked,
    goldenTowerUnlocked,
    innerLandMinesUnlocked,
    poisonSwampUnlocked,
    blackHoleUnlocked,
    spotlightUnlocked,
    standardResearch,
    tradeoffResearch,
    waveReductionResearch,
    perkOptionResearch,
    coinBonuses,
    coinsPerKill,
    coinsPerWave,
    goldenTowerBonus,
    goldenTowerDuration,
    goldenTowerCooldown,
    criticalCoin,
    gameSpeed,
    waveSkip,
    introSprint,
    spotlightCoins,
    spotlightAngle,
    spotlightQuantity,
    blackHoleCoins,
    blackHoleCooldown,
    blackHoleCoverage,
    blackHoleDuration,
    firstPerkChoice,
    bannedPerks,
    perkPriorityList,
    neverPickPerks,
  ]);
  return (
    <Provider
      value={{
        simulationCount,
        maxWave,
        smartMissilesUnlocked,
        deathWaveUnlocked,
        chainLightningUnlocked,
        chronoFieldUnlocked,
        goldenTowerUnlocked,
        innerLandMinesUnlocked,
        poisonSwampUnlocked,
        blackHoleUnlocked,
        spotlightUnlocked,

        standardResearch,
        tradeoffResearch,
        waveReductionResearch,
        perkOptionResearch,

        autopick,
        waveAccelerator,

        coinBonuses,
        coinsPerKill,
        coinsPerWave,
        goldenTowerBonus,
        goldenTowerDuration,
        goldenTowerCooldown,
        blackHoleCoverage,
        blackHoleCoins,
        blackHoleDuration,
        blackHoleCooldown,
        spotlightCoins,
        spotlightAngle,
        spotlightQuantity,
        criticalCoin,

        firstPerkChoice,
        bannedPerks,
        perkPriorityList,
        neverPickPerks,

        gameSpeed,
        waveSkip,
        introSprint,

        setSimulationCount,
        setMaxWave,

        setAutopick,
        setWaveAccelerator,

        setSmartMissilesUnlocked,
        setDeathWaveUnlocked,
        setChainLightningUnlocked,
        setChronoFieldUnlocked,
        setGoldenTowerUnlocked,
        setInnerLandMinesUnlocked,
        setPoisonSwampUnlocked,
        setBlackHoleUnlocked,
        setSpotlightUnlocked,

        setStandardResearch,
        setTradeoffResearch,
        setWaveReductionResearch,
        setPerkOptionResearch,

        setCoinBonuses,
        setCoinsPerKill,
        setCoinsPerWave,
        setGoldenTowerBonus,
        setGoldenTowerDuration,
        setGoldenTowerCooldown,
        setBlackHoleCoverage,
        setBlackHoleCoins,
        setBlackHoleDuration,
        setBlackHoleCooldown,
        setSpotlightAngle,
        setSpotlightCoins,
        setSpotlightQuantity,
        setCriticalCoin,

        setFirstPerkChoice,
        setBannedPerks,
        setPerkPriorityList,
        setNeverPickPerks,

        setGameSpeed,
        setWaveSkip,
        setIntroSprint,
      }}
    >
      {children}
    </Provider>
  );
};

export const useMaxWave = () =>
  useContextSelector(GameState, ({ maxWave }) => maxWave);
